import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useField } from 'react-final-form';
import { useLazyQuery } from '@apollo/client';
import { FORM_VALUES } from '../utils/helpers/constants';
import { getLandmarksQuery } from '../api/queries';
import { LandmarksQuery } from '../graphql/graphql';

export const useLandmarks = () => {
  const { change } = useForm();
  const { input: initialLandmarksField } = useField(
    FORM_VALUES.initialLandmarks,
    { subscription: { value: true } }
  );
  const { input: coordinatesField } = useField(FORM_VALUES.coordinates, {
    subscription: { value: true }
  });

  const [locations, setLocations] = useState<LandmarksQuery['landmarks']>(
    initialLandmarksField.value || []
  );

  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && initialLandmarksField.value?.length) {
      setLocations(initialLandmarksField.value);
      initialized.current = true;
    }
  }, [initialLandmarksField.value]);

  useEffect(() => {
    if (!coordinatesField.value) {
      setLocations([]);
      change(FORM_VALUES.locationIds, []);
    }
  }, [coordinatesField.value]);

  const [fetchLandmarks] = useLazyQuery<LandmarksQuery>(getLandmarksQuery, {
    onCompleted: data => {
      setLocations(data.landmarks);
      change(
        FORM_VALUES.locationIds,
        data.landmarks.map(l => l.id)
      );
    }
  });

  const getLandmarks = useCallback(
    (variables: {
      variables: { cityId: string; latitude: number; longitude: number };
    }) => fetchLandmarks(variables),
    [fetchLandmarks]
  );

  const handleLocationDelete = useCallback(
    (id: string) => {
      const updatedLocations =
        locations?.filter(location => location.id !== id) || [];
      setLocations(updatedLocations);
      change(
        FORM_VALUES.locationIds,
        updatedLocations.map(l => l.id)
      );
    },
    [locations, change]
  );

  return {
    locations,
    getLandmarks,
    handleLocationDelete
  };
};
